import { useMemo } from "react"
import type { ClipLayout } from "./constants"
import { generateWaveformBars } from "./constants"

export default function TimelineTrackAudio({
  clips,
  totalDuration,
  pxPerSec,
  audioCurrentTime,
  isPlaying,
}: {
  clips: ClipLayout[]
  totalDuration: number
  pxPerSec: number
  audioCurrentTime: number
  isPlaying: boolean
}) {
  const totalWidth = totalDuration * pxPerSec
  const barsPerSecond = 4
  const totalBars = Math.ceil(totalDuration * barsPerSecond)

  const waveformBars = useMemo(
    () => generateWaveformBars(999, totalBars),
    [totalBars],
  )

  const playedWidth = isPlaying ? audioCurrentTime * pxPerSec : 0

  return (
    <div className="relative h-10" style={{ width: totalWidth }}>
      {/* Played region tint */}
      {isPlaying && (
        <div
          className="absolute inset-y-0 left-0 bg-red/[0.06]"
          style={{ width: playedWidth }}
        />
      )}

      {/* Waveform bars */}
      <div className="flex h-full items-center gap-px px-0.5">
        {waveformBars.map((h, idx) => {
          const barProgress = idx / waveformBars.length
          const isPlayed = isPlaying && barProgress <= audioCurrentTime / totalDuration
          return (
            <div
              key={idx}
              className={`min-w-[2px] flex-1 rounded-sm ${isPlayed ? "bg-red/30" : "bg-stone/40"}`}
              style={{ height: `${h * 80}%` }}
            />
          )
        })}
      </div>

      {/* Scene boundary lines + duration labels */}
      {clips.map((clip, i) => (
        <div key={`segment-${clip.index}-${clip.type}`}>
          {i > 0 && (
            <div
              className="absolute inset-y-0 w-px bg-charcoal/10"
              style={{ left: clip.left }}
            />
          )}
          <div
            className="pointer-events-none absolute inset-y-0 flex items-center justify-center"
            style={{ left: clip.left, width: clip.width }}
          >
            <span className="rounded-full bg-red/80 px-1.5 py-px text-[9px] font-medium tabular-nums text-white">
              {clip.duration.toFixed(1)}s
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}
